import { LuPhone, LuWrench, LuGauge, LuCable, LuPlug } from "react-icons/lu";
import { business, services } from "../content/site";

/**
 * Icons for the hero quick-entry chips, keyed by service slug.
 * Presentation only — the label text always comes from site.js so the
 * service catalog stays the single source of truth.
 */
const chipIcons = {
  "electrical-repair": LuWrench,
  "panel-upgrades": LuGauge,
  "home-rewiring": LuCable,
  "switches-outlets": LuPlug,
};

/**
 * The four most common residential jobs, taken from the featured services
 * in site.js. Capped at four so the grid never crowds a small phone.
 */
const quickJobs = services.filter((service) => service.featured).slice(0, 4);

const Hero = () => (
  <section
    id="hero"
    aria-labelledby="hero-heading"
    className="on-inverse relative isolate overflow-hidden bg-surface-brand
               bg-gradient-to-br from-[color:var(--color-surface-brand)]
               to-[color:var(--color-surface-inverse)]
               text-ink-inverse"
  >
    {/*
      Hero background is built entirely from tokens — no image request, no
      missing asset. Two decorative layers: a fine diagonal rule pattern for
      texture and a soft corner glow for depth.
    */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          "repeating-linear-gradient(115deg, rgb(255 255 255 / 0.05) 0 1px, transparent 1px 4.5rem)",
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          "radial-gradient(60rem 30rem at 85% 0%, rgb(255 255 255 / 0.14), transparent 70%)",
      }}
    />

    <div className="container-page relative flex flex-col justify-center py-14 sm:py-16 lg:py-20 lg:min-h-[34rem]">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-12 lg:items-center">
        {/* --- Message column --- */}
        <div className="lg:col-span-7">
          <p className="kicker text-ink-inverse opacity-90">
            Residential and commercial electricians, Dallas-Fort Worth
          </p>

          <h1
            id="hero-heading"
            className="mt-4 text-display-lg text-ink-inverse [overflow-wrap:anywhere]"
          >
            Home electrical work, done right the first visit.
          </h1>

          <p className="mt-5 max-w-prose text-body-lg text-ink-inverse opacity-90">
            {business.shortName} handles the repairs, panel work, and rewiring
            that keep a North Texas home safe. Call and talk to an electrician
            about what is actually going on before you book anything.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href={business.phone.href}
              className="btn-urgent w-full sm:w-auto"
              aria-label={`Call ${business.shortName} at ${business.phone.display}`}
            >
              <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
              <span className="tabular">{business.phone.display}</span>
            </a>

            <a href="#contact" className="btn-outline-inverse w-full sm:w-auto">
              Request service
            </a>
          </div>
        </div>

        {/* --- Quick-entry column --- */}
        <div className="lg:col-span-5">
          {/*
            FUTURE PHOTO SLOT
            ------------------------------------------------------------------
            Drop a real photograph of the Mega Electric crew or a branded truck
            in RIGHT HERE, directly above the quick-job panel (or as a
            full-bleed layer behind this column). Competitor research found
            that neither reference site publishes genuine crew photography, so
            one honest photo of the actual people and vehicle is the single
            highest-value visual upgrade available to this hero. Requirements
            when it arrives: real image file committed to /public, descriptive
            alt text, explicit width/height to avoid layout shift, and
            loading="eager" since it is above the fold.
            ------------------------------------------------------------------
          */}
          <h2 className="text-label uppercase text-ink-inverse opacity-90">
            Common jobs we get called for
          </h2>

          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {quickJobs.map((service) => {
              const Icon = chipIcons[service.slug];
              return (
                <li key={service.slug} className="min-w-0">
                  <a
                    href="#services"
                    className="flex min-h-tap w-full items-center gap-3 rounded-md
                               border border-[rgb(255_255_255_/_0.28)]
                               bg-[rgb(255_255_255_/_0.08)] px-4 py-3
                               text-body-sm font-semibold text-ink-inverse
                               [overflow-wrap:anywhere]
                               transition-colors duration-base ease-standard
                               hover:bg-[rgb(255_255_255_/_0.18)]
                               active:bg-[rgb(255_255_255_/_0.26)]"
                  >
                    {Icon ? (
                      <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />
                    ) : null}
                    <span className="min-w-0">{service.title}</span>
                  </a>
                </li>
              );
            })}
          </ul>

          <p className="mt-4 text-body-sm text-ink-inverse opacity-90">
            <a
              href="#services"
              className="inline-flex min-h-tap items-center underline underline-offset-4"
            >
              See everything we work on
            </a>
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
